/**
 * 学习数据记录服务
 * ------------------------------------------------------------
 * 所有「学习痕迹」的读写入口：
 *   - sessions   一次练习的汇总（永久保留，永不覆盖）
 *   - answers    每题作答明细
 *   - errors     错题/薄弱点（聚合）
 *   - mastery    掌握矩阵（按 refType:refId + level 记录 pass/hint/fail）
 *
 * 其它模块不要直接操作这些集合，统一走这里，保证口径一致。
 */
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS } from '../core/schema.js';
import { state } from '../core/store.js';
import { emit, EVENTS } from '../core/events.js';
import { uid, nowISO, todayKey, daysAgo, uniq } from '../utils/index.js';

const sid = () => state.student?.id || '';
const masteryKey = (refType, refId) => `${refType}:${refId}`;

/* -------------------------------- 写入 -------------------------------- */

/**
 * 保存一次练习（结算时调用）
 * @param {object} summary PracticeSession.summary() 的返回值
 * @returns {Promise<object>} 已落库的 session
 */
export async function saveSession(summary) {
  const storage = getStorage();
  const studentId = sid();

  const session = {
    id: summary.id || uid('ses'),
    studentId,
    module: summary.module,
    title: summary.title || '',
    textbookId: summary.textbookId || '',
    unitId: summary.unitId || '',
    total: summary.total,
    correct: summary.correct,
    independent: summary.independent,
    hinted: summary.hinted,
    failed: summary.failed,
    accuracy: summary.accuracy,
    score: summary.score,
    bySkill: summary.bySkill || {},
    byLevel: summary.byLevel || {},
    startedAt: summary.startedAt,
    endedAt: summary.endedAt || nowISO(),
    durationMs: summary.durationMs || 0,
    xpEarned: summary.xpEarned || 0,
  };

  const answers = (summary.records || []).map(r => ({
    id: uid('ans'),
    sessionId: session.id,
    studentId,
    questionId: r.questionId,
    type: r.type,
    skill: r.skill,
    level: r.level,
    refType: r.refType,
    refId: r.refId,
    unitId: r.unitId || session.unitId,
    prompt: r.prompt,
    expected: r.expected,
    response: r.response,
    correct: r.correct,
    partial: r.partial,
    hintUsed: r.hintUsed,
    completion: r.completion,
    costMs: r.costMs,
    answeredAt: r.answeredAt,
  }));

  await storage.put(COLLECTIONS.SESSIONS, session);
  await storage.bulkPut(COLLECTIONS.ANSWERS, answers);
  await updateErrorsFromAnswers(answers, session);
  await updateMasteryFromAnswers(answers);

  emit(EVENTS.PROGRESS_CHANGED, session);
  return session;
}

/** 错题聚合：同一知识点同一级别累计，连续 2 次答对 → mastered */
async function updateErrorsFromAnswers(answers, session) {
  const storage = getStorage();
  const studentId = sid();
  const existing = await storage.query(COLLECTIONS.ERRORS, 'studentId', studentId);
  const map = new Map(existing.map(e => [e.id, e]));
  const touched = [];

  for (const a of answers) {
    if (!a.refId) continue;
    const key = `${a.refType || 'word'}:${a.refId}:L${a.level}`;
    const prev = map.get(key);

    if (a.correct) {
      if (!prev) continue;         // 没错过就不记录
      const history = [...(prev.history || []), { at: a.answeredAt, correct: true, hintUsed: a.hintUsed }];
      const streak = (prev.streak || 0) + 1;
      const next = {
        ...prev,
        history,
        streak,
        lastSeenAt: a.answeredAt,
        status: streak >= 2 ? 'mastered' : 'reviewing',
        masteredAt: streak >= 2 ? nowISO() : prev.masteredAt || null,
      };
      map.set(key, next);
      touched.push(next);
    } else {
      const history = [...(prev?.history || []), { at: a.answeredAt, correct: false, hintUsed: a.hintUsed }];
      const next = {
        id: key,
        studentId,
        refType: a.refType || 'word',
        refId: a.refId,
        level: a.level,
        unitId: a.unitId || session.unitId,
        textbookId: session.textbookId || '',
        skill: a.skill,
        prompt: a.prompt,
        expected: a.expected,
        wrongCount: (prev?.wrongCount || 0) + 1,
        streak: 0,
        status: 'active',
        history,
        firstWrongAt: prev?.firstWrongAt || a.answeredAt,
        lastSeenAt: a.answeredAt,
        masteredAt: null,
      };
      map.set(key, next);
      touched.push(next);
    }
  }

  if (touched.length) await storage.bulkPut(COLLECTIONS.ERRORS, touched);
}

/** 掌握矩阵：每级只保留最好状态 pass > hint > fail */
const RANK = { fail: 0, hint: 1, pass: 2 };

async function updateMasteryFromAnswers(answers) {
  const storage = getStorage();
  const studentId = sid();
  const touchedMap = new Map();

  for (const a of answers) {
    if (!a.refId) continue;
    const key = masteryKey(a.refType || 'word', a.refId);
    let row = touchedMap.get(key);
    if (!row) {
      row = (await storage.get(COLLECTIONS.MASTERY, key)) || {
        key, studentId,
        refType: a.refType || 'word',
        refId: a.refId,
        unitId: a.unitId || '',
        textbookId: '',
        levels: {},
        updatedAt: nowISO(),
      };
      touchedMap.set(key, row);
    }
    const lv = String(a.level ?? 1);
    const status = a.correct ? (a.hintUsed ? 'hint' : 'pass') : 'fail';
    const prev = row.levels[lv];
    // fail 也记录（这样才能看到"哪一级出错"），但不会覆盖更好的结果
    if (!prev || RANK[status] > RANK[prev] || status === 'fail') {
      // fail 只在没有更好的记录时写入，避免一次失误降级历史最好成绩
      if (status === 'fail' && prev && RANK[prev] > RANK.fail) continue;
      row.levels[lv] = status;
    }
    row.updatedAt = nowISO();
    if (!row.textbookId && a.unitId) row.textbookId = a.textbookId || row.textbookId;
  }

  if (touchedMap.size) await storage.bulkPut(COLLECTIONS.MASTERY, [...touchedMap.values()]);
}

/* -------------------------------- 读取 -------------------------------- */

/** 历史成绩（按结束时间倒序，永久保留） */
export async function listSessions({ unitId = '', module = '', limit = 0 } = {}) {
  const storage = getStorage();
  let rows = (await storage.list(COLLECTIONS.SESSIONS)).filter(s => s.studentId === sid());
  if (unitId) rows = rows.filter(s => s.unitId === unitId);
  if (module) rows = rows.filter(s => s.module === module);
  rows.sort((a, b) => new Date(b.endedAt) - new Date(a.endedAt));
  return limit ? rows.slice(0, limit) : rows;
}

/** 某次练习的答题明细 */
export async function answersOf(sessionId) {
  const storage = getStorage();
  const rows = await storage.query(COLLECTIONS.ANSWERS, 'sessionId', sessionId);
  return rows.sort((a, b) => (a.level - b.level));
}

/** 某单元的掌握矩阵行 [{ key, refType, refId, levels }] */
export async function masteryOf(unitId) {
  const storage = getStorage();
  const rows = await storage.query(COLLECTIONS.MASTERY, 'unitId', unitId);
  return rows.filter(r => r.studentId === sid() || !r.studentId);
}

/** 某单元的错题（默认不含已掌握） */
export async function errorsOf(unitId, { includeMastered = false } = {}) {
  const storage = getStorage();
  const rows = await storage.query(COLLECTIONS.ERRORS, 'unitId', unitId);
  return rows
    .filter(e => e.studentId === sid())
    .filter(e => includeMastered || e.status !== 'mastered')
    .sort((a, b) => b.wrongCount - a.wrongCount);
}

/** 全部待复习知识点（跨单元），用于「今日复习」 */
export async function dueErrors(limit = 0) {
  const storage = getStorage();
  const rows = (await storage.list(COLLECTIONS.ERRORS))
    .filter(e => e.studentId === sid() && e.status !== 'mastered')
    .sort((a, b) => {
      const wa = a.wrongCount || 0;
      const wb = b.wrongCount || 0;
      if (wb !== wa) return wb - wa;
      return new Date(a.lastSeenAt || 0) - new Date(b.lastSeenAt || 0);
    });
  return limit ? rows.slice(0, limit) : rows;
}

/** 连续学习天数 */
export async function studyStreak() {
  const sessions = await listSessions();
  const days = uniq(sessions.map(s => (s.endedAt || '').slice(0, 10))).filter(Boolean);
  if (!days.length) return 0;
  const set = new Set(days);
  let streak = 0;
  for (let i = 0; i < 400; i++) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    if (set.has(key)) streak++;
    else if (i > 0) break;
  }
  return streak;
}

/** 今日已完成题量 / 正确率 */
export async function todayStats() {
  const sessions = await listSessions();
  const today = todayKey();
  const todays = sessions.filter(s => (s.endedAt || '').slice(0, 10) === today);
  const total = todays.reduce((n, s) => n + (s.total || 0), 0);
  const correct = todays.reduce((n, s) => n + (s.correct || 0), 0);
  return { sessions: todays.length, total, correct, accuracy: total ? Math.round(correct / total * 100) : 0 };
}

/** 单元掌握率（0~1）：所有出现过的 level 中 pass 的比例 */
export async function unitMasteryRate(unitId) {
  const rows = await masteryOf(unitId);
  let pass = 0, all = 0;
  rows.forEach(r => {
    Object.values(r.levels || {}).forEach(v => { all++; if (v === 'pass') pass++; });
  });
  return all ? pass / all : 0;
}

/** 近 N 次练习的成绩序列（进步曲线） */
export async function scoreTrend({ unitId = '', module = '', n = 12 } = {}) {
  const rows = await listSessions({ unitId, module, limit: n });
  return rows.reverse().map(s => ({
    date: s.endedAt,
    score: s.score,
    accuracy: s.accuracy,
    module: s.module,
    title: s.title,
  }));
}

/** 最近 N 天内出错的记录（复习页用） */
export async function recentErrors(days = 3) {
  const rows = await dueErrors();
  return rows.filter(e => daysAgo(e.lastSeenAt) <= days);
}
